// Vendor Libs
import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';

// Dish Tech React
import {ActionButton, DataTable} from '@dishtech-react/core';
import {useService} from '@dishtech-react/hooks';
import {areRolesAuthorized} from '@dishtech-react/utils';

// Material UI
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import {Button, FormControlLabel, Radio, RadioGroup, TableCell, TableRow} from '@material-ui/core';
import {green, red} from '@material-ui/core/colors';
import {makeStyles} from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';

// Configs
import {tableConfig} from '../../../config/table-config';

// Components
import CustomSearch from './custom-search';

const SERVICE_KEY = 'ASSET';

const useStyles = makeStyles(theme => ({
  addButton: {
    color: green[500],
  },
  innerTableContainer: {
    width: '300px',
  },
  removeButton: {
    color: red[500],
  },
  table: {
    marginTop: theme.spacing(2),
  },
  dialogFooter: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '40px',
    '& p': {
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      paddingLeft: '20px',
    },
  },
  selectionCheckbox: {display: 'flex', marginBottom: 10},
  toolbarButton: {margin: 6, marginRight: 15},
  toolbarIcon: {color: 'green'},
}));

const AssetDataTable = ({
  canAdd,
  canRemove,
  data,
  onAdd,
  onRemove,
  reorderEnabled,
  rippleRoles,
  showSelectionControls,
  showToolbar,
  sort,
  total,
}) => {
  const {
    params: {limit = 25, offset = 0, keyword = '', sortField = 'title', sortDirection = 1} = {},
    setParamValue,
    setParamValues,
  } = useService(SERVICE_KEY);
  const tableRef = useRef(null);
  const classes = useStyles();
  const {
    [SERVICE_KEY]: {columns, headerConfig},
    DATA_PACKAGE: {columns: dataPackageColumns, headerConfig: dataPackageHeaderConfig},
  } = tableConfig;
  const [serverFilterList, setServerFilterList] = useState(columns.map(() => []));
  const muiHandleSearch = useRef(null);
  const searchText = useRef(null);
  const [multipleAdd, setMultipleAdd] = useState('none');
  const [selectedFolderData, setSelectedFolderData] = useState([]);

  const getEchostarId = rowData => rowData[columns.findIndex(({name}) => name === 'echostarId')];

  const getDataPackageId = rowData => rowData[dataPackageColumns.findIndex(({name}) => name === 'dataPackageId')];

  const getDataPackage = (rowEchostarId, rowDataPackageId) => {
    const asset = data.find(({echostarId}) => echostarId === rowEchostarId);

    return {
      ...asset.dataPackages.find(({dataPackageId}) => dataPackageId === rowDataPackageId),
      asset: {...asset, dataPackages: []},
    };
  };

  const renderAddIcon = echostarId => (_value, {rowData}) =>
    !reorderEnabled ? (
      <ActionButton
        handleClick={() => onAdd(getDataPackage(echostarId, getDataPackageId(rowData)))}
        icon={AddCircleIcon}
        iconButton={classes.addButton}
        toolTipTitle="Add Data Package"
      />
    ) : (
      <Button disabled={reorderEnabled} startIcon={<AddCircleIcon />} />
    );

  const renderRemoveIcon = (_value, {rowData}) => (
    <ActionButton
      handleClick={() => onRemove(getDataPackageId(rowData))}
      icon={RemoveCircleIcon}
      iconButton={classes.removeButton}
      toolTipTitle="Remove Data Package"
    />
  );

  const isKeywordEqual = isSearch =>
    isSearch &&
    ((!searchText.current && keyword === '') || (searchText.current && searchText.current.value === keyword));

  const updateSearch = (filterList, curOffset, isSearch) => () => {
    if (!isKeywordEqual(isSearch) || !isEqual(filterList, serverFilterList)) {
      const params = {offset: curOffset, limit, sortField, sortDirection};

      columns.forEach(({name}, index) => {
        if (filterList[index].length) {
          [params[name]] = filterList[index];
        }
      });

      if (searchText.current && searchText.current.value.length) {
        params.keyword = searchText.current.value;
      }

      setParamValues(params).then(() => {
        setServerFilterList(filterList);

        if (isSearch) {
          // reset the mui page number to 0
          muiHandleSearch.current(searchText.current ? searchText.current.value : '');
        }
      });
    }
  };

  const setOrder = (fieldName, direction) => {
    setParamValues({
      limit,
      offset,
      keyword,
      sortField: fieldName,
      sortDirection: direction === 'ascending' ? 1 : 2,
    });
  };

  const renderExpandableRow = rowData => (
    <TableRow>
      <TableCell colSpan="4">
        <div className={classes.innerTableContainer}>
          <DataTable
            backgroundColor="#fffff0"
            columnConfig={[
              ...dataPackageColumns,
              ...(canAdd || canRemove
                ? [
                    {
                      name: '',
                      options: {
                        customBodyRender: canAdd ? renderAddIcon(getEchostarId(rowData)) : renderRemoveIcon,
                        download: false,
                        empty: true,
                        filter: false,
                        sort: false,
                        viewColumns: false,
                        pagination: false,
                      },
                    },
                  ]
                : []),
            ]}
            data={data
              .find(({echostarId}) => echostarId === getEchostarId(rowData))
              .dataPackages.map(dataPackage => ({
                ...dataPackage,
                est: !!dataPackage.est,
              }))}
            headerBackgroundColor="#efefe0"
            headerConfig={dataPackageHeaderConfig}
            idKey="name"
            itemLabel="Asset"
            minWidth={`${tableRef.current ? tableRef.current.offsetWidth - 32 : 536}px`}
            options={{
              expandableRows: false,
              pagination: false,
              responsive: 'scrollFullHeight',
              selectableRows: 'none',
            }}
            showToolbar={false}
          />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className={classes.table} ref={tableRef}>
      {showSelectionControls === true ? (
        <div className={classes.selectionCheckbox}>
          <FormControl component="fieldset">
            <RadioGroup row={true} value={multipleAdd}>
              <FormControlLabel
                control={<Radio />}
                label="Single package selection"
                value="none"
                onChange={() => {
                  setMultipleAdd('none');
                  setSelectedFolderData([]);
                }}
              />
              <FormControlLabel
                control={<Radio />}
                label="Multiple packages selection"
                value="multiple"
                onChange={() => setMultipleAdd('multiple')}
              />
            </RadioGroup>
          </FormControl>
        </div>
      ) : null}
      <DataTable
        columnConfig={columns.map(({options, ...column}, index) => ({
          ...column,
          options: {filterList: serverFilterList[index], ...options},
        }))}
        data={data}
        headerConfig={headerConfig}
        idKey="name"
        itemLabel="Asset"
        minWidth="550px"
        options={{
          count: total,
          customFilterDialogFooter: filterList => (
            <div className={classes.dialogFooter}>
              <Button variant="contained" onClick={updateSearch(filterList, 0, false)}>
                Apply Filters
              </Button>
              <p>Click outside of dialog to close</p>
            </div>
          ),
          customSearchRender: (_searchData, handleSearch, hideSearch) => {
            muiHandleSearch.current = handleSearch;

            return (
              <CustomSearch
                searchText={searchText}
                onHide={hideSearch}
                onSearch={() => updateSearch(serverFilterList, 0, true)()}
              />
            );
          },
          expandableRows: true,
          expandableRowsOnClick: true,
          onChangePage: page => {
            setParamValue('offset', page * limit);
          },
          onChangeRowsPerPage: numberOfRows => {
            setParamValue('limit', numberOfRows);
          },
          onColumnSortChange: (changedColumn, _direction) => {
            setOrder(changedColumn === 'name' ? 'title' : changedColumn, _direction);
          },
          onFilterChange: (changedColumn, _filterList, type) => {
            if (type === 'reset') {
              updateSearch(
                serverFilterList.map(() => []),
                offset,
                false
              )();
            } else if (type === 'chip') {
              updateSearch(
                columns.map(({name}, index) => (name === changedColumn ? [] : serverFilterList[index])),
                0,
                false
              )();
            }
          },
          onSearchClose: () => {
            searchText.current = undefined;
            updateSearch(serverFilterList, 0, true)();
          },
          renderExpandableRow,
          responsive: 'scrollFullHeight',
          rowsPerPage: limit,
          selectableRows: multipleAdd,
          serverSide: true,
          serverSideFilterList: serverFilterList,
          sort,
          rowsSelected: selectedFolderData || [],
          customToolbarSelect: () => {
            if (selectedFolderData?.length) {
              return (
                <Button
                  className={classes.toolbarButton}
                  startIcon={<AddCircleIcon className={classes.toolbarIcon} />}
                  toolTipTitle="Add Data Package"
                  variant="outlined"
                  onClick={() => {
                    const foundPackages = data?.filter((_, index) => selectedFolderData?.includes(index));
                    if (foundPackages) {
                      foundPackages.forEach(f =>
                        f?.dataPackages.forEach(d => onAdd(getDataPackage(f?.echostarId, d?.dataPackageId)))
                      );
                    }
                  }}
                >
                  Add Packages from selected Echostar IDs
                </Button>
              );
            }
          },
          textLabels: showToolbar
            ? {}
            : {
                body: {
                  noMatch: !areRolesAuthorized(rippleRoles, 'FOLDER', ['UPDATE'])
                    ? 'Asset list empty'
                    : 'Asset list empty, add from Assets on the right.',
                },
              },
          viewColumns: false,
          onRowsSelect: (_, rows) => setSelectedFolderData(rows.map(r => r.dataIndex)),
        }}
        showToolbar={showToolbar}
        title="Assets"
      />
    </div>
  );
};

AssetDataTable.propTypes = {
  canAdd: PropTypes.bool,
  canRemove: PropTypes.bool,
  data: PropTypes.array,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  reorderEnabled: PropTypes.bool,
  rippleRoles: PropTypes.array,
  showSelectionControls: PropTypes.bool,
  showToolbar: PropTypes.bool,
  sort: PropTypes.bool,
  total: PropTypes.number,
};

AssetDataTable.defaultProps = {
  canAdd: false,
  canRemove: false,
  showToolbar: true,
  sort: true,
};

export default AssetDataTable;
